import { useState } from 'react'
import { useForm } from '@inertiajs/react'
import { MoreHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu'
import type { Proposal, ApprovalCopy } from '@/frontend/features/Fde/WorkApprovals/types'

export default function DecisionForm({ proposal, copy }: { proposal: Proposal; copy: ApprovalCopy }) {
  const form = useForm({ revision: proposal.revision, decision: '', reason: '' })
  // Only the editor mode/draft is local. The parent remounts this form when
  // project, proposal, signed revision or canonical decision changes on a poll.
  const [reasonFor, setReasonFor] = useState<'approve' | 'adjust' | 'reject' | null>(null)
  const reasonLabel = { approve: copy.ui.approval_note, adjust: copy.ui.adjust_reason, reject: copy.ui.reject_reason }
  const submitLabel = { approve: copy.approve, adjust: copy.ui.send_adjustment, reject: copy.ui.confirm_rejection }
  function decide(decision: string) {
    form.transform(data => ({ ...data, decision }))
    form.patch(proposal.path, { preserveScroll: true, preserveState: false })
  }
  if (!proposal.can_decide) return null
  return <section className="space-y-3" aria-label={copy.ui.decision}>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap gap-2">
        {!reasonFor && <>
          <Button disabled={form.processing} onClick={() => decide('approve')}>{form.processing ? copy.ui.saving : copy.approve}</Button>
          <Button variant="outline" disabled={form.processing} onClick={() => setReasonFor('adjust')}>{copy.adjust}</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" aria-label={copy.ui.more} title={copy.ui.more} disabled={form.processing}><MoreHorizontal className="size-4" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setReasonFor('approve')}>{copy.ui.approve_with_note}</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => decide('defer')}>{copy.defer}</DropdownMenuItem>
              <DropdownMenuItem variant="destructive" onSelect={() => setReasonFor('reject')}>{copy.reject}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>}
      </div>
      {!reasonFor && <p className="text-xs text-muted-foreground">{copy.authorized_by}: {proposal.approvers.join(', ')}</p>}
    </div>
    {reasonFor && <form className="space-y-3" onSubmit={e => { e.preventDefault(); decide(reasonFor) }}>
      <label className="block space-y-2" htmlFor="approval-reason">
        <span className="text-sm font-medium">{reasonLabel[reasonFor]}</span>
        <Textarea id="approval-reason" autoFocus required={reasonFor !== 'approve'} disabled={form.processing} value={form.data.reason} onChange={e => form.setData('reason', e.target.value)} />
      </label>
      <div className="flex gap-2">
        <Button disabled={form.processing || (reasonFor !== 'approve' && !form.data.reason.trim())}>{form.processing ? copy.ui.saving : submitLabel[reasonFor]}</Button>
        <Button type="button" variant="ghost" disabled={form.processing} onClick={() => { setReasonFor(null); form.reset('reason') }}>{copy.ui.cancel}</Button>
      </div>
    </form>}
    {Object.values(form.errors).map(error => <p key={error} role="alert" className="text-sm text-destructive">{error}</p>)}
  </section>
}
